import React from 'react';
import Icon from '../../../components/AppIcon';

const MarketSentiment = ({ market }) => {
  const sentimentData = {
    overall: 'bullish',
    score: 68,
    trend: 'up',
    confidence: 82,
    participants: 1247,
    lastUpdate: '5 minutes ago'
  };

  const sentimentBreakdown = [
    {
      sentiment: 'Very Bullish',
      percentage: 28,
      count: 349,
      color: 'bg-green-600'
    },
    {
      sentiment: 'Bullish',
      percentage: 40,
      count: 499,
      color: 'bg-green-400'
    },
    {
      sentiment: 'Neutral',
      percentage: 18,
      count: 224,
      color: 'bg-gray-400'
    },
    {
      sentiment: 'Bearish',
      percentage: 10,
      count: 125,
      color: 'bg-red-400'
    },
    {
      sentiment: 'Very Bearish',
      percentage: 4,
      count: 50,
      color: 'bg-red-600'
    }
  ];

  const aiInsights = [
    {
      title: 'Trading Volume Analysis',
      insight: 'Trading volumes 15% higher than weekly average, indicating strong market participation',
      impact: 'positive',
      confidence: 87
    },
    {
      title: 'News Sentiment',
      insight: 'Recent policy announcements driving positive sentiment among market participants',
      impact: 'positive',
      confidence: 79
    },
    {
      title: 'Price Momentum',
      insight: 'Upward price momentum sustained over past 72 hours with minimal corrections',
      impact: 'positive',
      confidence: 91
    },
    {
      title: 'Risk Factor',
      insight: 'International market volatility could impact domestic prices in medium term',
      impact: 'negative',
      confidence: 65
    }
  ];

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'bullish') return 'text-green-600';
    if (sentiment === 'bearish') return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      {/* Overall Sentiment */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Brain" size={20} className="text-primary" />
            AI-Powered Market Sentiment
          </h3>
          <span className="text-xs text-muted-foreground">
            Updated {sentimentData?.lastUpdate}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Overall Sentiment</span>
              <Icon name="TrendingUp" size={16} className={getSentimentColor(sentimentData?.overall)} />
            </div>
            <div className={`text-2xl font-bold capitalize ${getSentimentColor(sentimentData?.overall)}`}>
              {sentimentData?.overall}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 bg-muted rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${sentimentData?.score}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-foreground">{sentimentData?.score}/100</span>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Confidence Level</span>
              <Icon name="Target" size={16} className="text-primary" />
            </div>
            <div className="text-2xl font-bold text-foreground">{sentimentData?.confidence}%</div>
            <div className="mt-2">
              <span className="text-xs text-muted-foreground">
                Based on {sentimentData?.participants?.toLocaleString()} participants
              </span>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Market Trend</span>
              <Icon
                name={sentimentData?.trend === 'up' ? 'ArrowUp' : 'ArrowDown'}
                size={16}
                className={sentimentData?.trend === 'up' ? 'text-green-500' : 'text-red-500'}
              />
            </div>
            <div className={`text-2xl font-bold ${sentimentData?.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {sentimentData?.trend === 'up' ? 'Upward' : 'Downward'}
            </div>
            <div className="mt-2">
              <span className="text-xs text-muted-foreground">
                Momentum: {sentimentData?.trend === 'up' ? 'Strong' : 'Weak'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Sentiment Breakdown */}
      <div>
        <h4 className="font-semibold text-foreground mb-4">Sentiment Distribution</h4>
        <div className="space-y-3">
          {sentimentBreakdown?.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground font-medium">{item?.sentiment}</span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{item?.count} traders</span>
                  <span className="font-semibold text-foreground">{item?.percentage}%</span>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-3">
                <div
                  className={`${item?.color} h-3 rounded-full transition-all duration-500`}
                  style={{ width: `${item?.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Insights */}
      <div>
        <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Sparkles" size={18} className="text-primary" />
          AI-Generated Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {aiInsights?.map((insight, index) => (
            <div
              key={index}
              className={`rounded-lg p-4 border ${
                insight?.impact === 'positive' ?'bg-green-50/50 border-green-200' :'bg-red-50/50 border-red-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h5 className="font-semibold text-foreground text-sm">{insight?.title}</h5>
                <Icon
                  name={insight?.impact === 'positive' ? 'ThumbsUp' : 'AlertTriangle'}
                  size={16}
                  className={insight?.impact === 'positive' ? 'text-green-600' : 'text-red-600'}
                />
              </div>
              <p className="text-sm text-foreground mb-3">{insight?.insight}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Confidence:</span>
                <div className="flex-1 bg-muted rounded-full h-1.5">
                  <div
                    className={`${insight?.impact === 'positive' ? 'bg-green-500' : 'bg-red-500'} h-1.5 rounded-full`}
                    style={{ width: `${insight?.confidence}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-foreground">{insight?.confidence}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Market Participants */}
      <div className="bg-primary/5 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Users" size={20} className="text-primary" />
            <div>
              <div className="font-semibold text-foreground">
                {sentimentData?.participants?.toLocaleString()} Active Participants
              </div>
              <div className="text-sm text-muted-foreground">
                Contributing to market sentiment analysis
              </div>
            </div>
          </div>
          <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
            Share Your View
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketSentiment;